import { useCallback } from 'react';

import { BigPictureClient, Event } from '@vimeo/bigpicture-client';
import { usePage } from '@vimeo/core/hooks/use-api-resource/PageProvider';

import { useAnalyticsEventContext } from './AnalyticsEventProvider';

interface GlobalAnalyticsData {
  path: string;
  entry_page: string;
  page: string;
}

export interface BPEventType<BPEventNames, CustomEventData, CommonEventData> {
  eventName: BPEventNames;
  version: number;
  fields?: CustomEventData;
  defaultEventFields?: CommonEventData;
  globalAnalyticsToInclude?: GlobalAnalyticsProperty[];
}

type GlobalAnalyticsProperty = 'path' | 'entry_page' | 'page' | 'product';

/**
 * @deprecated Avoid adding new uses of this hook. It makes it difficult / impossible
 * to properly type all the data we ultimately send to Big Picture. We should try
 * to avoid obscuring the call to BigPictureClient.sendEvent behind layers of functions
 * so that the compiler can properly infer types. Simplicity is our friend here.
 */
export function useAnalyticsEvent() {
  // AnalyticsEventContext contains data that we inject from parent component in the DOM and request from the API
  const analyticsEventContext = useAnalyticsEventContext();

  const page = usePage();

  const globalAnalyticsData: GlobalAnalyticsData = {
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    // TODO account for referrers within react-router history
    entry_page: typeof window !== 'undefined' ? getEntryPage(window.document.referrer) : '',
    page,
  };
  const sendAnalyticEvent = useCallback(
    <CustomEventData extends Record<string, any>, CommonEventData extends Record<string, any>>({
      eventName,
      version,
      fields = {} as CustomEventData,
      defaultEventFields = {} as CommonEventData,
      globalAnalyticsToInclude = ['path', 'entry_page', 'page', 'product'],
    }: BPEventType<string, CustomEventData, CommonEventData>): void => {
      /**
       * Big Picture events must have all keys included in their schema with no additional keys
       * This assigns all contextual data to existing fields and removes non existant ones
       */
      const defaultFieldsWithAnalyticsEventContext = {};
      Object.keys(defaultEventFields).map(function (key) {
        if (analyticsEventContext.hasOwnProperty(key)) {
          (defaultFieldsWithAnalyticsEventContext as any)[key] = analyticsEventContext[key];
        } else {
          (defaultFieldsWithAnalyticsEventContext as any)[key] = defaultEventFields[key];
        }
      });

      const includedGlobalAnalytics = {};
      globalAnalyticsToInclude.forEach((property) => {
        if (property === 'product') {
          (includedGlobalAnalytics as any)[property] = analyticsEventContext.product ?? '';
        } else {
          (includedGlobalAnalytics as any)[property] = globalAnalyticsData[property];
        }
      });

      BigPictureClient.sendEvent(
        // @ts-expect-error A refactor of this hook is coming down the pike.
        new Event(eventName, version, {
          ...includedGlobalAnalytics,
          ...defaultFieldsWithAnalyticsEventContext,
          ...fields,
        }),
      );
    },
    [Object.values(globalAnalyticsData).join(), Object.values(analyticsEventContext).join()],
  );

  return sendAnalyticEvent;
}

// Javascript version of controllers_v6/VideoSettingsController.php function _getEntryScreen
export const getEntryPage = (referrer: string): string => {
  // Using (\d+) insead of matching clip_id directly
  if (/manage\/videos\/(\d+)/i.test(referrer)) {
    return 'single_video_view';
  }
  if (/\/manage\/folders/i.test(referrer)) {
    return 'folders';
  }
  if (/\/manage\/showcases/i.test(referrer)) {
    return 'showcase_cms';
  }
  if (/\/upload/i.test(referrer)) {
    return 'upload';
  }
  if (/\/manage\/videos/i.test(referrer)) {
    return 'video_manager';
  }
  if (/\/manage\/events/i.test(referrer)) {
    return 'event_manager';
  }
  if (/\/create\/templates/i.test(referrer)) {
    return 'templates';
  }
  if (/\/review\/(\d+)/i.test(referrer)) {
    return 'review_page';
  }
  if (/\/user\/(\d+)\/folder\/(\d+)/i.test(referrer)) {
    return 'mvv';
  }
  if (/\/(\d+)/i.test(referrer)) {
    return 'clip_page';
  }
  /**
   * We need to account for dangling `/`s and language subfolders on the Vimeo homepage,
   * e.g. https://vimeo.com/fr/
   */
  // Remove domain specific characters from the string
  const topLevelWithPath = referrer.split('.').pop() as string;
  if (/(com|dev)\/?(es|de|fr|jp|pt-br|ko)?\/?$/i.test(topLevelWithPath)) {
    return 'lihp';
  }
  return referrer;
};
